import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board } from '@cortex/domain-org-issues';
import { PRIORITIES, SHARINGS } from '@cortex/domain-org-issues/model';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import {
  MultiPicker,
  type MultiPickerOption,
} from '@cortex/web-design-system/components/ui/MultiPicker';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import {
  useMoveOrgIssue,
  useOrgBoardMembers,
  useOrgIssue,
  useSetOrgIssueSharing,
  useUpdateOrgIssue,
} from '../../../hooks/useOrgIssues.js';
import type { OrgIssueDetail, OrgIssuePeople } from '../../../lib/api/org-issues.js';
import {
  changedFields,
  parseLabels,
  personLabel,
  personOptions,
  toIssueDraft,
} from '../../../lib/org-issues.js';
import { ActionsEditor, LinksEditor } from './ActionsEditor.js';
import { IssueActivity } from './IssueActivity.js';

/** @graph-connects none */
interface IssueDetailModalProps {
  board: Board;
  issueId: string | null;
  onClose: () => void;
}

/**
 * 課題の詳細。本文・Next・リンク・決着メモの編集、列・ピン留め・共有状態の変更、コメント・履歴。
 *
 * 操作の出し分けは API が返す `permissions`（`canEdit` / `canMove` / `canManageSharing`）だけで決める。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題 1 件を開き、中身の編集・状態（列）の変更・ピン留めと共有状態の変更・コメントと履歴の確認を 1 か所で行う
 * @graph-connects useOrgIssue [calls] 課題の詳細
 * @graph-connects useOrgBoardMembers [calls] 担当の候補
 */
export function IssueDetailModal({ board, issueId, onClose }: IssueDetailModalProps): ReactNode {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useOrgIssue(board.id, issueId);
  const members = useOrgBoardMembers(board.id);

  return (
    <Modal
      open={issueId !== null}
      onClose={onClose}
      size="full"
      title={data?.issue.title ?? t('orgIssues.common.loading')}
    >
      {isLoading && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.common.loading')}</p>
      )}
      {isError && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {error.message}
        </p>
      )}
      {data !== undefined && (
        <div className="space-y-6">
          <IssueStateControls detail={data} />
          <IssueContentEditor
            key={data.issue.id}
            detail={data}
            candidates={candidateOptions(
              { ...data.people, ...members.data?.people },
              members.data?.members ?? [],
              data,
            )}
          />
          <IssueActivity detail={data} />
        </div>
      )}
    </Modal>
  );
}

/**
 * 担当の候補。ボードを見られる在籍者に、今の担当（退職者や公開範囲を狭めた後の人）を足す
 * （足さないと、選び直したときに黙って外れる）。
 * @graph-connects personOptions [calls] 候補の形
 */
function candidateOptions(
  people: OrgIssuePeople,
  members: readonly string[],
  detail: OrgIssueDetail,
): MultiPickerOption[] {
  const current = [
    ...detail.issue.assignees,
    ...detail.issue.actions.flatMap((action) => action.assigneeIds),
  ];
  return personOptions(people, [...new Set([...members, ...current])]);
}

/**
 * 列（状態）・ピン留め・共有状態。どれも押した時点で API に送る。
 * @graph-connects useMoveOrgIssue [calls] 列の移動
 * @graph-connects useSetOrgIssueSharing [calls] ピン留め・共有状態の変更
 */
function IssueStateControls({ detail }: { detail: OrgIssueDetail }): ReactNode {
  const { t } = useTranslation();
  const { board, issue, permissions } = detail;
  const move = useMoveOrgIssue(board.id);
  const sharing = useSetOrgIssueSharing(board.id);
  const columns = [...board.columns].sort((a, b) => a.position - b.position);
  const failure = move.error ?? sharing.error;

  return (
    <div className="space-y-2">
      <div className="grid gap-3 md:grid-cols-3">
        <Select
          label={t('orgIssues.issue.column')}
          value={issue.columnId}
          disabled={!permissions.canMove || move.isPending}
          options={columns.map((column) => ({ value: column.id, label: column.label }))}
          onChange={(e) => {
            move.mutate({ issueId: issue.id, columnId: e.target.value });
          }}
        />
        <Select
          label={t('orgIssues.issue.sharing')}
          helperText={t('orgIssues.issue.sharingHelp')}
          value={issue.sharing}
          disabled={!permissions.canManageSharing || sharing.isPending}
          options={SHARINGS.map((value) => ({ value, label: t(`orgIssues.sharing.${value}`) }))}
          onChange={(e) => {
            const next = SHARINGS.find((value) => value === e.target.value);
            if (next !== undefined) sharing.mutate({ issueId: issue.id, input: { sharing: next } });
          }}
        />
        <label className="flex items-center gap-2 self-center text-sm text-[var(--color-text-primary)]">
          <Checkbox
            checked={issue.pinned}
            disabled={!permissions.canManageSharing || sharing.isPending}
            onChange={(e) => {
              sharing.mutate({ issueId: issue.id, input: { pinned: e.target.checked } });
            }}
          />
          {t('orgIssues.issue.pinned')}
        </label>
      </div>
      {failure !== null && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {failure.message}
        </p>
      )}
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('orgIssues.issue.created', {
          name: personLabel(detail.people, issue.createdBy),
          date: new Date(issue.createdAt).toLocaleDateString(),
        })}{' '}
        · {t(`orgIssues.issue.source.${issue.source.kind}`)}
        {issue.source.kind === 'notion_import' && (
          <>
            {' '}
            ·{' '}
            <a
              href={issue.source.notionUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[var(--color-accent-primary)] underline"
            >
              {t('orgIssues.issue.openNotion')}
            </a>
          </>
        )}
      </p>
    </div>
  );
}

/** @graph-connects none */
interface IssueContentEditorProps {
  detail: OrgIssueDetail;
  candidates: MultiPickerOption[];
}

/**
 * 課題の中身。**書き換えた項目だけを送る**（他の人が別の項目を書き換えた直後でも上書きしない）。
 * @graph-connects useUpdateOrgIssue [calls] 課題の中身の更新
 * @graph-connects changedFields [calls] 書き換えた項目の差分
 */
function IssueContentEditor({ detail, candidates }: IssueContentEditorProps): ReactNode {
  const { t } = useTranslation();
  const { issue, permissions } = detail;
  const update = useUpdateOrgIssue(detail.board.id);
  const [draft, setDraft] = useState(() => toIssueDraft(issue));
  const [labelsText, setLabelsText] = useState(issue.labels.join(', '));
  const [saved, setSaved] = useState(false);
  const readOnly = !permissions.canEdit;
  const patch = changedFields(issue, { ...draft, labels: parseLabels(labelsText) });
  const hasChanges = Object.keys(patch).length > 0;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { issueId: issue.id, patch },
          {
            onSuccess: (view) => {
              setDraft(toIssueDraft(view.issue));
              setLabelsText(view.issue.labels.join(', '));
              setSaved(true);
            },
          },
        );
      }}
      onChange={() => {
        setSaved(false);
      }}
    >
      {readOnly && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.issue.readOnly')}</p>
      )}
      <Input
        label={t('orgIssues.issue.title')}
        value={draft.title}
        disabled={readOnly}
        fullWidth
        onChange={(e) => {
          setDraft({ ...draft, title: e.target.value });
        }}
      />
      <div className="grid gap-3 md:grid-cols-3">
        <Select
          label={t('orgIssues.issue.priority')}
          value={draft.priority ?? ''}
          disabled={readOnly}
          options={[
            { value: '', label: t('orgIssues.priority.none') },
            ...PRIORITIES.map((value) => ({ value, label: t(`orgIssues.priority.${value}`) })),
          ]}
          onChange={(e) => {
            const next = PRIORITIES.find((value) => value === e.target.value) ?? null;
            setDraft({ ...draft, priority: next });
          }}
        />
        <MultiPicker
          label={t('orgIssues.issue.assignees')}
          placeholder={t('orgIssues.issue.assigneesPlaceholder')}
          options={candidates}
          value={draft.assignees}
          searchable
          disabled={readOnly}
          onChange={(assignees) => {
            setSaved(false);
            setDraft({ ...draft, assignees });
          }}
        />
        <Input
          label={t('orgIssues.issue.labels')}
          value={labelsText}
          disabled={readOnly}
          onChange={(e) => {
            setLabelsText(e.target.value);
          }}
        />
      </div>
      <label className="block space-y-1 text-sm text-[var(--color-text-secondary)]">
        <span>{t('orgIssues.issue.body')}</span>
        <Textarea
          rows={10}
          value={draft.body}
          disabled={readOnly}
          onChange={(e) => {
            setDraft({ ...draft, body: e.target.value });
          }}
        />
      </label>
      <ActionsEditor
        actions={draft.actions}
        candidates={candidates}
        disabled={readOnly}
        onChange={(actions) => {
          setSaved(false);
          setDraft({ ...draft, actions });
        }}
      />
      <LinksEditor
        links={draft.links}
        disabled={readOnly}
        onChange={(links) => {
          setSaved(false);
          setDraft({ ...draft, links });
        }}
      />
      <label className="block space-y-1 text-sm text-[var(--color-text-secondary)]">
        <span>{t('orgIssues.issue.resolutionNote')}</span>
        <Textarea
          rows={3}
          value={draft.resolutionNote}
          disabled={readOnly}
          onChange={(e) => {
            setDraft({ ...draft, resolutionNote: e.target.value });
          }}
        />
      </label>
      {update.isError && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {update.error.message}
        </p>
      )}
      {!readOnly && (
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!hasChanges} loading={update.isPending}>
            {t('orgIssues.issue.save')}
          </Button>
          {saved && !hasChanges && (
            <span className="text-sm text-[var(--color-text-muted)]">
              {t('orgIssues.issue.saved')}
            </span>
          )}
        </div>
      )}
    </form>
  );
}
